import React, { useState, useEffect } from 'react';
import { userService, talentScheduleService, talentService } from '../../../api/apiService';
import Select from 'react-select';
import { format } from 'date-fns';
import '../../../App.css';

function SetVacationPage() {
  const [talentIds, setTalentIds] = useState([]);
  const [talentData, setTalentData] = useState([]);
  const [selectedTalent, setSelectedTalent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [availabilityStatus, setAvailabilityStatus] = useState('available');
  const [schedules, setSchedules] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const customStyles = {
    dropdownIndicator: (provided, state) => ({
      ...provided,
      border: 'none',
    }),
    control: (base) => ({
      ...base,
      width: '400px',
      borderColor: 'black',
      borderWidth: '1px',
      boxShadow: 'none',
      margin: '5px',
      borderRadius: '0px',
      color: 'black',
      fontWeight: "700",
      height: '50px',
      textTransform: 'uppercase',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'black',
      fontWeight: "700"
    }),
    input: (provided) => ({
      ...provided,
      color: 'black',
      fontWeight: "700"
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'black' : 'inherit',
      '&:hover': { backgroundColor: state.isSelected ? 'black' : '#f7f7f7' }
    }),
  };

  useEffect(() => {
    const fetchUserTalents = async () => {
      try {
        setLoading(true);
        
        // Get user profile
        const userProfileResponse = await userService.getProfile();
        const userData = userProfileResponse?.data?.user;
        
        if (!userData) {
          console.error("User data not found");
          setError("Failed to load user data");
          setLoading(false);
          return;
        }
        
        console.log("User profile data:", userData);
        
        // Extract talent IDs from user profile
        let fetchedTalentIds = [];
        if (userData.talents && Array.isArray(userData.talents)) {
          fetchedTalentIds = userData.talents.map(talent => 
            talent.db_id ? talent.db_id.toString() : ''
          ).filter(id => id);
        }
        
        setTalentIds(fetchedTalentIds);
        
        // Fetch talent details for each ID
        const talentsData = [];
        for (const id of fetchedTalentIds) {
          try {
            const talentResponse = await talentService.getTalentById(id);
            if (talentResponse?.data) {
              // Extract name and function from the response
              const talentName = talentResponse.data.name || talentResponse.data.Name || 'Unknown Name';
              const talentFunction = talentResponse.data.function || talentResponse.data.Funktion || 
                                     talentResponse.data.job || talentResponse.data.position || 'No Function';
              
              talentsData.push({
                id,
                name: talentName,
                function: talentFunction
              });
            }
          } catch (err) {
            console.error(`Error fetching talent ${id}:`, err);
          }
        }
        
        // Sort talents by name
        const sortedTalents = talentsData.sort((a, b) => {
          const nameA = a.name || 'Unknown';
          const nameB = b.name || 'Unknown';
          return nameA.localeCompare(nameB);
        });
        
        setTalentData(sortedTalents);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching user talents:", err);
        setError("Failed to load talent information");
        setLoading(false);
      }
    };

    fetchUserTalents();
  }, []);

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        if (!talentIds.length) return;
        
        setLoading(true);
        const currentDate = new Date();
        const currentDateStr = format(currentDate, 'yyyy-MM-dd');
        
        // Use future date (e.g., 1 year from now) for the end date range
        const futureDate = new Date();
        futureDate.setFullYear(futureDate.getFullYear() + 1);
        const futureDateStr = format(futureDate, 'yyyy-MM-dd');
        
        const allSchedules = [];
        
        // Fetch schedules for each talent
        for (const talentId of talentIds) {
          try {
            // Use the talent schedule service to get availabilities and vacations
            const scheduleResponse = await talentScheduleService.getTalentScheduleByRange(
              talentId, 
              currentDateStr, 
              futureDateStr
            );
            
            const scheduleData = scheduleResponse?.data?.schedule;
            
            if (scheduleData) {
              // Add availabilities to the schedules list
              if (scheduleData.availabilities && scheduleData.availabilities.length > 0) {
                const availabilities = scheduleData.availabilities.map(avail => ({
                  id: avail.id,
                  talentId: talentId,
                  startDate: avail.start_date,
                  endDate: avail.end_date,
                  type: 'available',
                  lastUpdatedAt: avail.updated_at,
                  updatedBy: avail.updated_by || 'Unknown'
                }));
                allSchedules.push(...availabilities);
              }
              
              // Add vacations to the schedules list
              if (scheduleData.vacations && scheduleData.vacations.length > 0) {
                const vacations = scheduleData.vacations.map(vac => ({
                  id: vac.id,
                  talentId: talentId,
                  startDate: vac.start_date,
                  endDate: vac.end_date,
                  type: 'on vacation',
                  lastUpdatedAt: vac.updated_at,
                  updatedBy: vac.updated_by || 'Unknown'
                }));
                allSchedules.push(...vacations);
              }
            }
          } catch (err) {
            console.error(`Error fetching schedules for talent ${talentId}:`, err);
          }
        }
        
        setSchedules(allSchedules);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching schedules:", err);
        setError("Failed to load schedule information");
        setLoading(false);
      }
    };

    if (talentIds.length > 0) {
      fetchSchedules();
    }
  }, [talentIds]);

  useEffect(() => {
    if (editingSchedule) {
      setSelectedTalent(editingSchedule.talentId);
      setStartDate(formatDateForInput(editingSchedule.startDate));
      setEndDate(formatDateForInput(editingSchedule.endDate));
      setAvailabilityStatus(editingSchedule.type === 'available' ? 'available' : 'on vacation');
    }
  }, [editingSchedule]);

  const formatDateForInput = (dateString) => {
    try {
      if (!dateString) return '';
      const date = new Date(dateString);
      return format(date, 'yyyy-MM-dd');
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return format(date, 'dd.MM.yyyy');
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return 'Invalid date';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        talent_id: selectedTalent,
        start_date: startDate,
        end_date: endDate
      };

      if (availabilityStatus === 'available') {
        if (editingSchedule && editingSchedule.type === 'on vacation') {
          await talentScheduleService.deleteVacation(editingSchedule.id);
          await talentScheduleService.addAvailability(data);
        } else if (editingSchedule && editingSchedule.type === 'available') {
          await talentScheduleService.updateAvailability(selectedTalent, data);
        } else {
          await talentScheduleService.addAvailability(data);
        }
      } else {
        if (editingSchedule && editingSchedule.type === 'available') {
          await talentScheduleService.deleteAvailability(editingSchedule.id);
          await talentScheduleService.addVacation(data);
        } else if (editingSchedule && editingSchedule.type === 'on vacation') {
          await talentScheduleService.updateVacation(editingSchedule.id, data);
        } else {
          await talentScheduleService.addVacation(data);
        }
      }

      const currentDate = new Date();
      const currentDateStr = format(currentDate, 'yyyy-MM-dd');
      
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 1);
      const futureDateStr = format(futureDate, 'yyyy-MM-dd');
      
      const allSchedules = [];
      
      for (const talentId of talentIds) {
        try {
          const scheduleResponse = await talentScheduleService.getTalentScheduleByRange(
            talentId, 
            currentDateStr, 
            futureDateStr
          );
          
          const scheduleData = scheduleResponse?.data?.schedule;
          
          if (scheduleData) {
            if (scheduleData.availabilities && scheduleData.availabilities.length > 0) {
              const availabilities = scheduleData.availabilities.map(avail => ({
                id: avail.id,
                talentId: talentId,
                startDate: avail.start_date,
                endDate: avail.end_date,
                type: 'available',
                lastUpdatedAt: avail.updated_at,
                updatedBy: avail.updated_by || 'Unknown'
              }));
              allSchedules.push(...availabilities);
            }
            
            if (scheduleData.vacations && scheduleData.vacations.length > 0) {
              const vacations = scheduleData.vacations.map(vac => ({
                id: vac.id,
                talentId: talentId,
                startDate: vac.start_date,
                endDate: vac.end_date,
                type: 'on vacation',
                lastUpdatedAt: vac.updated_at,
                updatedBy: vac.updated_by || 'Unknown'
              }));
              allSchedules.push(...vacations);
            }
          }
        } catch (err) {
          console.error(`Error refreshing schedules for talent ${talentId}:`, err);
        }
      }
      
      setSchedules(allSchedules);
      setEditingSchedule(null);
      
      setSelectedTalent('');
      setStartDate('');
      setEndDate('');
      setAvailabilityStatus('available');
      
      alert(editingSchedule ? 'Schedule updated successfully!' : 'Schedule set successfully!');
    } catch (error) {
      console.error('Error setting schedule: ', error);
      alert('Error updating schedule. Please try again.');
    }
  };

  const handleDelete = async (scheduleId, type) => {
    try {
      if (type === 'available') {
        await talentScheduleService.deleteAvailability(scheduleId);
      } else if (type === 'on vacation') {
        await talentScheduleService.deleteVacation(scheduleId);
      }
      
      setSchedules(schedules.filter(schedule => schedule.id !== scheduleId));
      alert('Schedule deleted successfully!');
      return true;
    } catch (error) {
      console.error('Error deleting schedule: ', error);
      alert('Error deleting schedule. Please try again.');
      return false;
    }
  };

  const handleCancelEdit = () => {
    setEditingSchedule(null);
    setSelectedTalent('');
    setStartDate('');
    setEndDate('');
    setAvailabilityStatus('available');
  };

  const talentOptions = talentData.map(talent => ({
    value: talent.id,
    label: `${talent.name} - ${talent.function}`
  }));
  
  const availabilityOptions = [
    { value: 'available', label: 'Available' },
    { value: 'on vacation', label: 'Not Available' }
  ];

  if (loading) {
    return (
      <section>
        <div className="content-wrapper">
          <h1>Set Talent Schedule</h1>
          <div className="loading">Loading talent information...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <div className="content-wrapper">
          <h1>Set Talent Schedule</h1>
          <div className="error">
            <p>{error}</p>
            <p>Please try refreshing the page or contact support if the problem persists.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className='content-wrapper'>
        {editingSchedule ? (
          <>
            <h1>Edit Schedule</h1>
            <p style={{ color: 'red', marginBottom: '20px' }}>
              Note: If you have already accepted a job during these dates, your update will not be accepted.
            </p>
          </>
        ) : (
          <h1>Set Talent Schedule</h1>
        )}
        <form onSubmit={handleSubmit} style={{ marginTop: '40px', maxWidth: '500px' }}>
          <div>
            <Select
              options={talentOptions}
              value={talentOptions.find(option => option.value === selectedTalent)}
              onChange={option => setSelectedTalent(option.value)}
              styles={customStyles}
              placeholder="Select Talent"
              required
            />
          </div>
      
          <div className='availability-dropdown-container'>
            <Select
              className='availability-dropdown'
              options={availabilityOptions}
              required
              value={availabilityOptions.find(option => option.value === availabilityStatus)}
              onChange={option => setAvailabilityStatus(option.value)}
              styles={customStyles}
              placeholder="Select Status"
            />
          </div>

          <div className="talent-datepicker-container" style={{ display: 'flex' }}>
            <div className="date-input-wrapper">
              <input 
                required 
                className='inputdate' 
                id="start-date" 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
                required 
              />
              <svg className="custom-icon" viewBox="0 0 500 500" >
                <path d="M477.5,39h-105V21.5c0-6.25-3.34-12.03-8.75-15.16s-12.09-3.12-17.5,0s-8.75,8.9-8.75,15.16V39h-70V21.5  c0-6.25-3.34-12.03-8.75-15.16s-12.09-3.12-17.5,0s-8.75,8.9-8.75,15.16V39h-70V21.5c0-6.25-3.34-12.03-8.75-15.16  s-12.09-3.12-17.5,0s-8.75,8.9-8.75,15.16V39h-105c-4.64,0-9.09,1.84-12.38,5.12S5,51.86,5,56.5v420c0,4.64,1.84,9.09,5.12,12.38  S17.86,494,22.5,494h455c4.64,0,9.09-1.84,12.38-5.12s5.12-7.73,5.12-12.38v-420c0-4.64-1.84-9.09-5.12-12.38S482.14,39,477.5,39z   M127.5,74v17.5c0,6.25,3.34,12.03,8.75,15.16s12.09,3.12,17.5,0s8.75-8.9,8.75-15.16V74h70v17.5c0,6.25,3.34,12.03,8.75,15.16  s12.09,3.12,17.5,0s8.75-8.9,8.75-15.16V74h70v17.5c0,6.25,3.34,12.03,8.75,15.16s12.09,3.12,17.5,0s8.75-8.9,8.75-15.16V74H460v70  H40V74H127.5z M40,459V179h420v280H40z M302.5,249c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70  c-6.25,0-12.03-3.34-15.16-8.75c-3.12-5.41-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12  S302.5,244.36,302.5,249z M425,249c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75  c-3.12-5.41-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S425,244.36,425,249z M180,249  c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75c-3.12-5.41-3.12-12.09,0-17.5  s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S180,244.36,180,249z M302.5,319c0,4.64-1.84,9.09-5.12,12.38  s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12  S302.5,314.36,302.5,319z M425,319c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75  s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S425,314.36,425,319z M180,319c0,4.64-1.84,9.09-5.12,12.38  s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12  S180,314.36,180,319z M302.5,389c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75  s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S302.5,384.36,302.5,389z M425,389  c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70  c4.64,0,9.09,1.84,12.38,5.12S425,384.36,425,389z M180,389c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70  c-6.25,0-12.03-3.34-15.16-8.75s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S180,384.36,180,389z" />
              </svg>
            </div>

            <div className="date-input-wrapper">
              <input 
                required 
                className='inputdate' 
                id="end-date" 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
                required 
                placeholder='END' 
              />
              <svg className="custom-icon" viewBox="0 0 500 500" >
                <path d="M477.5,39h-105V21.5c0-6.25-3.34-12.03-8.75-15.16s-12.09-3.12-17.5,0s-8.75,8.9-8.75,15.16V39h-70V21.5  c0-6.25-3.34-12.03-8.75-15.16s-12.09-3.12-17.5,0s-8.75,8.9-8.75,15.16V39h-70V21.5c0-6.25-3.34-12.03-8.75-15.16  s-12.09-3.12-17.5,0s-8.75,8.9-8.75,15.16V39h-105c-4.64,0-9.09,1.84-12.38,5.12S5,51.86,5,56.5v420c0,4.64,1.84,9.09,5.12,12.38  S17.86,494,22.5,494h455c4.64,0,9.09-1.84,12.38-5.12s5.12-7.73,5.12-12.38v-420c0-4.64-1.84-9.09-5.12-12.38S482.14,39,477.5,39z   M127.5,74v17.5c0,6.25,3.34,12.03,8.75,15.16s12.09,3.12,17.5,0s8.75-8.9,8.75-15.16V74h70v17.5c0,6.25,3.34,12.03,8.75,15.16  s12.09,3.12,17.5,0s8.75-8.9,8.75-15.16V74h70v17.5c0,6.25,3.34,12.03,8.75,15.16s12.09,3.12,17.5,0s8.75-8.9,8.75-15.16V74H460v70  H40V74H127.5z M40,459V179h420v280H40z M302.5,249c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70  c-6.25,0-12.03-3.34-15.16-8.75c-3.12-5.41-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12  S302.5,244.36,302.5,249z M425,249c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75  c-3.12-5.41-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S425,244.36,425,249z M180,249  c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75c-3.12-5.41-3.12-12.09,0-17.5  s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S180,244.36,180,249z M302.5,319c0,4.64-1.84,9.09-5.12,12.38  s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12  S302.5,314.36,302.5,319z M425,319c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75  s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S425,314.36,425,319z M180,319c0,4.64-1.84,9.09-5.12,12.38  s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12  S180,314.36,180,319z M302.5,389c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75  s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S302.5,384.36,302.5,389z M425,389  c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70c-6.25,0-12.03-3.34-15.16-8.75s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70  c4.64,0,9.09,1.84,12.38,5.12S425,384.36,425,389z M180,389c0,4.64-1.84,9.09-5.12,12.38s-7.73,5.12-12.38,5.12h-70  c-6.25,0-12.03-3.34-15.16-8.75s-3.12-12.09,0-17.5s8.9-8.75,15.16-8.75h70c4.64,0,9.09,1.84,12.38,5.12S180,384.36,180,389z" />
              </svg>
            </div>
          </div>
          <div style={{ marginTop: '20px' }}>
            <button className='big-button' type="submit">
              {editingSchedule ? 'Update Schedule' : 'Set Schedule'}
            </button>
            {editingSchedule && (
              <button 
                type="button" 
                onClick={handleCancelEdit}
                className='big-button'
                style={{ marginLeft: '10px', backgroundColor: '#666' }}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
        
        <div style={{ marginTop: '40px' }}>
          <h2>Current Schedules</h2>
          {schedules.length === 0 ? (
            <p>No schedules found. Add your first schedule above.</p>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {schedules.map(schedule => {
                const talent = talentData.find(t => t.id === schedule.talentId);
                
                return (
                  <div key={schedule.id} style={{
                    border: '1px solid #ddd',
                    padding: '15px',
                    margin: '10px 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <p><strong>Talent:</strong> {talent ? `${talent.name} - ${talent.function}` : 'Unknown'}</p>
                      <p><strong>Status:</strong> {schedule.type}</p>
                      <p><strong>Period:</strong> {formatDate(schedule.startDate)} - {formatDate(schedule.endDate)}</p>
                      {schedule.lastUpdatedAt && (
                        <p style={{ fontSize: '0.9em', color: '#666' }}>
                          <strong>Last updated:</strong> {formatDate(schedule.lastUpdatedAt)}
                          {schedule.updatedBy && ` by ${schedule.updatedBy}`}
                        </p>
                      )}
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                      <button 
                        onClick={() => setEditingSchedule(schedule)}
                        style={{ marginRight: '10px', marginBottom: '10px' }}
                        className='big-button'
                      >
                        Edit
                      </button>
                      <button  
                        onClick={() => handleDelete(schedule.id, schedule.type)}
                        className='big-button'
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default SetVacationPage;